import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OverallMetrics } from '@/types/reports';
import { ExpenseCategorySummary } from '@/types/finance';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { formatTaxSuffix } from '@/utils/tax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface FinancialSummaryTableProps {
  metrics: OverallMetrics | undefined;
  expenseSummary: ExpenseCategorySummary[];
  totalExpenses: number;
  isLoading: boolean;
}

interface SummaryRow {
  label: string;
  amount: number;
  isSubtotal?: boolean;
  isTotal?: boolean;
  indent?: number;
  isNegative?: boolean;
  onClick?: () => void;
  isClickable?: boolean;
}

export const FinancialSummaryTable = ({
  metrics,
  expenseSummary,
  totalExpenses,
  isLoading
}: FinancialSummaryTableProps) => {
  const [expensesExpanded, setExpensesExpanded] = useState(false);
  const tax = useCompanyTax();
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const incSuffix = tax.enabled ? ` (${formatTaxSuffix(tax)})` : '';
  const exSuffix = tax.enabled ? ` (ex. ${tax.label})` : '';

  if (isLoading || !metrics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Financial Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-muted animate-pulse rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  // Gross profit: net collected revenue minus all direct costs
  const grossProfit = metrics.turnoverAfterTax - metrics.partsCost - metrics.tollCost -
    metrics.cardProcessingFees - metrics.techPay - metrics.managersCommission;

  // Net operating profit (gross profit - campaign costs - operating expenses)
  const netOperatingProfit = grossProfit - metrics.campaignCost - totalExpenses;

  // Income rows — totalTurnover is now the sum of real payments only (no WFP)
  const incomeRows: SummaryRow[] = [
    { label: `Total Invoiced${incSuffix}`, amount: metrics.totalInvoiced },
    { label: `Collected Revenue${incSuffix}`, amount: metrics.totalTurnover, isSubtotal: true },
    ...(tax.enabled
      ? [
          { label: `Less: ${tax.label} on Collected`, amount: -metrics.gstOwed, isNegative: true },
          { label: `Net Revenue${exSuffix}`, amount: metrics.turnoverAfterTax, isSubtotal: true },
        ]
      : []),
  ];

  const directCostRows: SummaryRow[] = [
    { label: 'Parts Cost', amount: -metrics.partsCost, indent: 1, isNegative: true },
    { label: 'Toll Costs', amount: -metrics.tollCost, indent: 1, isNegative: true },
    { label: 'Card Processing Fees', amount: -metrics.cardProcessingFees, indent: 1, isNegative: true },
    { label: 'Technician Commission', amount: -metrics.techPay, indent: 1, isNegative: true },
    { label: 'Manager Overrides', amount: -metrics.managersCommission, indent: 1, isNegative: true },
    { label: 'Gross Profit', amount: grossProfit, isSubtotal: true },
  ];

  const renderRow = (row: SummaryRow, index: number) => (
    <div
      key={index}
      onClick={row.onClick}
      className={cn(
        "flex justify-between items-center py-2 px-3",
        row.isSubtotal && "bg-muted/50 font-medium border-t",
        row.isTotal && "bg-primary/5 font-bold border-t-2 border-primary/20",
        row.indent === 1 && "pl-6",
        row.isClickable && "cursor-pointer hover:bg-muted/50 transition-colors"
      )}
    >
      <span className={cn(
        "text-sm",
        row.isSubtotal && "font-medium",
        row.isTotal && "font-bold text-base",
      )}>
        {row.label}
      </span>
      <span className={cn(
        "text-sm font-mono",
        row.isNegative && !row.isSubtotal && !row.isTotal && "text-destructive",
        row.isSubtotal && "font-medium",
        row.isTotal && row.amount >= 0 && "font-bold text-base text-success",
        row.isTotal && row.amount < 0 && "font-bold text-base text-destructive",
      )}>
        {row.isNegative && !row.isSubtotal && !row.isTotal ? '−' : ''}
        {formatCurrency(Math.abs(row.amount))}
      </span>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Financial Summary (P&L)</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {/* Income Section */}
          <div className="p-4 pb-0">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Income
            </h4>
          </div>
          <div className="divide-y">
            {incomeRows.map(renderRow)}
          </div>

          {/* Direct Costs Section */}
          <div className="p-4 pb-0 pt-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Direct Costs
            </h4>
          </div>
          <div className="divide-y">
            {directCostRows.map(renderRow)}
          </div>

          {/* Operating Expenses Section */}
          <div className="p-4 pb-0 pt-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Operating Expenses
            </h4>
          </div>
          <div className="divide-y">
            {/* Always show Advertising */}
            {renderRow({ label: 'Advertising & Marketing', amount: -metrics.campaignCost, indent: 1, isNegative: true }, 0)}

            {/* Expandable company expenses */}
            {expenseSummary.length > 0 && (
              <>
                {/* Toggle header row */}
                <button
                  onClick={() => setExpensesExpanded(v => !v)}
                  className="w-full flex justify-between items-center py-2 px-3 pl-6 hover:bg-muted/40 transition-colors text-left"
                >
                  <span className="text-sm flex items-center gap-1.5 text-muted-foreground">
                    {expensesExpanded
                      ? <ChevronDown className="h-3.5 w-3.5" />
                      : <ChevronRight className="h-3.5 w-3.5" />}
                    Company Expenses
                    <span className="text-xs opacity-60">({expenseSummary.length} categor{expenseSummary.length === 1 ? 'y' : 'ies'})</span>
                  </span>
                  <span className="text-sm font-mono text-destructive">
                    −{formatCurrency(totalExpenses)}
                  </span>
                </button>

                {/* Expanded category rows */}
                {expensesExpanded && (
                  <div className="bg-muted/20 divide-y">
                    {expenseSummary.map((exp, i) => (
                      <div key={i} className="flex justify-between items-center py-1.5 px-3 pl-10">
                        <span className="text-xs text-muted-foreground">{exp.label}</span>
                        <span className="text-xs font-mono text-destructive">
                          −{formatCurrency(exp.total)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Net Operating Profit */}
            <div
              className="flex justify-between items-center py-2 px-3 bg-primary/5 font-bold border-t-2 border-primary/20"
            >
              <span className="font-bold text-base">Net Operating Profit</span>
              <span className={cn(
                "text-base font-bold font-mono",
                netOperatingProfit >= 0 ? "text-success" : "text-destructive"
              )}>
                {formatCurrency(netOperatingProfit)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>

    </Card>
  );
};
